import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import {
  apiGetGuardian, apiSetGuardian, apiRemoveGuardian,
  apiGetPrivacy, apiUpdatePrivacy, apiExportData, apiDeleteAccount,
} from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Shield, Download, UserCheck, Eye, Trash2, Lock, Heart } from "lucide-react";
import { motion } from "framer-motion";

export default function SettingsPage() {
  const { token, user, logout } = useAuth();
  const nav = useNavigate();

  // Guardian
  const [guardian, setGuardian] = useState({ guardian_name: "", guardian_email: "", guardian_message: "", guardian_set: false });
  const [gName, setGName] = useState("");
  const [gEmail, setGEmail] = useState("");
  const [gMessage, setGMessage] = useState("");
  const [savingG, setSavingG] = useState(false);

  // Privacy
  const [privacy, setPrivacy] = useState({ chat_visible_to_guardian: true, diary_visible_to_guardian: false, photos_visible_to_guardian: true, auto_delete_after_years: null });
  const [savingP, setSavingP] = useState(false);

  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!token) return;
    Promise.all([apiGetGuardian(token), apiGetPrivacy(token)]).then(([g, p]) => {
      setGuardian(g);
      setGName(g.guardian_name || "");
      setGEmail(g.guardian_email || "");
      setGMessage(g.guardian_message || "");
      setPrivacy(p);
    }).catch(() => {});
  }, [token]);

  const saveGuardian = async () => {
    if (!gName.trim() || !gEmail.trim()) { toast.error("Inserisci nome e email del custode"); return; }
    setSavingG(true);
    try {
      await apiSetGuardian(token, { guardian_name: gName, guardian_email: gEmail, guardian_message: gMessage });
      setGuardian({ ...guardian, guardian_name: gName, guardian_email: gEmail, guardian_message: gMessage, guardian_set: true });
      toast.success("Custode designato");
    } catch { toast.error("Errore"); }
    setSavingG(false);
  };

  const removeGuardian = async () => {
    if (!window.confirm("Rimuovere il custode designato?")) return;
    try {
      await apiRemoveGuardian(token);
      setGuardian({ guardian_name: "", guardian_email: "", guardian_message: "", guardian_set: false });
      setGName(""); setGEmail(""); setGMessage("");
      toast.success("Custode rimosso");
    } catch { toast.error("Errore"); }
  };

  const savePrivacy = async () => {
    setSavingP(true);
    try {
      await apiUpdatePrivacy(token, privacy);
      toast.success("Impostazioni privacy salvate");
    } catch { toast.error("Errore"); }
    setSavingP(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const data = await apiExportData(token);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `iocisono_backup_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup scaricato");
    } catch { toast.error("Errore nell'esportazione"); }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    const confirm1 = window.confirm("Sei sicuro di voler eliminare il tuo account? Tutti i dati verranno persi per sempre.");
    if (!confirm1) return;
    const confirm2 = window.confirm("Questa azione e irreversibile. Confermi?");
    if (!confirm2) return;
    try {
      await apiDeleteAccount(token);
      logout();
      nav("/");
      toast.success("Account eliminato");
    } catch { toast.error("Errore"); }
  };

  return (
    <div className="min-h-screen bg-gradient-hero" data-testid="settings-page">
      <header className="border-b border-border/40 bg-card/60 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-6 py-3.5 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="settings-back">
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Link>
          <h1 className="font-serif text-base font-bold text-foreground">Impostazioni</h1>
          <div className="w-20" />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-8 space-y-6">

        {/* Guardian Section */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0 }}>
          <Card className="bg-card border-border/30 shadow-soft" data-testid="guardian-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <UserCheck className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h2 className="font-serif text-lg font-bold text-foreground">Custode designato</h2>
                  <p className="text-[11px] text-muted-foreground">La persona che potra attivare la tua presenza digitale quando non ci sarai piu</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Nome del custode</Label>
                  <Input placeholder="Es. Maria Rossi" value={gName} onChange={(e) => setGName(e.target.value)} data-testid="guardian-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Email del custode</Label>
                  <Input type="email" placeholder="custode@email.it" value={gEmail} onChange={(e) => setGEmail(e.target.value)} data-testid="guardian-email" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Messaggio per il custode (opzionale)</Label>
                  <Textarea placeholder="Un messaggio personale che il custode ricevera quando sara il momento..." value={gMessage}
                    onChange={(e) => setGMessage(e.target.value)} rows={3} data-testid="guardian-message" />
                </div>
                <div className="flex items-center gap-3">
                  <Button variant="premium" onClick={saveGuardian} disabled={savingG} data-testid="save-guardian">
                    {savingG ? "Salvataggio..." : guardian.guardian_set ? "Aggiorna custode" : "Designa custode"}
                  </Button>
                  {guardian.guardian_set && (
                    <Button variant="ghost" size="sm" onClick={removeGuardian} className="text-muted-foreground hover:text-destructive text-xs">
                      Rimuovi
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Privacy Section */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
          <Card className="bg-card border-border/30 shadow-soft" data-testid="privacy-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h2 className="font-serif text-lg font-bold text-foreground">Privacy e visibilita</h2>
                  <p className="text-[11px] text-muted-foreground">Controlla cosa sara visibile al tuo custode</p>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm text-foreground">Conversazioni chat</p>
                    <p className="text-[11px] text-muted-foreground">Il custode potra leggere le tue conversazioni</p>
                  </div>
                  <Switch checked={privacy.chat_visible_to_guardian}
                    onCheckedChange={(v) => setPrivacy({ ...privacy, chat_visible_to_guardian: v })} data-testid="privacy-chat" />
                </div>
                <div className="w-full h-px bg-border/30" />
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm text-foreground">Diario personale</p>
                    <p className="text-[11px] text-muted-foreground">Il custode potra leggere le tue voci di diario</p>
                  </div>
                  <Switch checked={privacy.diary_visible_to_guardian}
                    onCheckedChange={(v) => setPrivacy({ ...privacy, diary_visible_to_guardian: v })} data-testid="privacy-diary" />
                </div>
                <div className="w-full h-px bg-border/30" />
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className="text-sm text-foreground">Foto caricate</p>
                    <p className="text-[11px] text-muted-foreground">Il custode potra vedere le foto</p>
                  </div>
                  <Switch checked={privacy.photos_visible_to_guardian}
                    onCheckedChange={(v) => setPrivacy({ ...privacy, photos_visible_to_guardian: v })} data-testid="privacy-photos" />
                </div>
                <Button variant="outline-primary" size="sm" onClick={savePrivacy} disabled={savingP} className="mt-2" data-testid="save-privacy">
                  {savingP ? "Salvataggio..." : "Salva impostazioni"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Data & Backup Section */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="bg-card border-border/30 shadow-soft" data-testid="backup-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <Download className="w-5 h-5 text-emerald-500" />
                </div>
                <div>
                  <h2 className="font-serif text-lg font-bold text-foreground">Backup e dati</h2>
                  <p className="text-[11px] text-muted-foreground">Scarica una copia completa di tutti i tuoi dati</p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground mb-4">
                Il backup include: profili, conversazioni, voci di diario, impostazioni. Formato JSON.
              </p>
              <Button variant="outline-primary" onClick={handleExport} disabled={exporting} data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                {exporting ? "Esportazione..." : "Scarica backup completo"}
              </Button>
            </CardContent>
          </Card>
        </motion.div>

        {/* Conservation Policy Link */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
          <Card className="bg-card border-border/30 shadow-soft cursor-pointer hover:shadow-medium transition-shadow"
            onClick={() => nav("/policy")} data-testid="policy-link">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Shield className="w-5 h-5 text-blue-500" />
              </div>
              <div className="flex-1">
                <h2 className="font-serif text-base font-bold text-foreground">Policy di conservazione</h2>
                <p className="text-[11px] text-muted-foreground">Come proteggiamo e conserviamo i tuoi ricordi nel tempo</p>
              </div>
              <ArrowLeft className="w-4 h-4 text-muted-foreground/30 rotate-180" />
            </CardContent>
          </Card>
        </motion.div>

        {/* Danger Zone */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <Card className="bg-card border-destructive/20 shadow-soft" data-testid="danger-section">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-destructive/8 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-destructive/60" />
                </div>
                <div>
                  <h2 className="font-serif text-base font-bold text-foreground">Zona pericolosa</h2>
                  <p className="text-[11px] text-muted-foreground">Azioni irreversibili</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Eliminando il tuo account, tutti i profili, le conversazioni e i ricordi verranno cancellati per sempre.
              </p>
              <Button variant="ghost" size="sm" onClick={handleDeleteAccount}
                className="text-destructive/60 hover:text-destructive hover:bg-destructive/5 text-xs" data-testid="delete-account-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Elimina account e tutti i dati
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
